import { ChangeDetectionStrategy, Component, EventEmitter, Output, computed, inject, input, signal } from '@angular/core';
import { DropdownComponent, DropdownItem, DropdownItemPrefixDirective, DropdownTriggerDirective } from '@libs/ui-lib';
import { ProgressNoteStatus } from '@ffd/api-client';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { SvgIcons, SvgIconComponent } from '@ngneat/svg-icon';

interface StatusOption {
  labelKey: string;
  value: ProgressNoteStatus;
  bgClass: string;
}

@Component({
  selector: 'lib-select-status',
  templateUrl: './select-status.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DropdownComponent, DropdownItemPrefixDirective, DropdownTriggerDirective, TranslatePipe, SvgIconComponent],
})
export class SelectStatusComponent {
  private readonly translateService = inject(TranslateService);

  public triggerType = input<'small' | 'normal'>('normal');

  public selectedStatus = input.required<ProgressNoteStatus>();
  public selectedStatusOption = computed(() => this.getStatusOption(this.selectedStatus()));

  @Output() public statusChange = new EventEmitter<ProgressNoteStatus>();

  public readonly dropdownOpen = signal(false);
  protected readonly dropdownIcon = computed<SvgIcons>(() =>
    this.dropdownOpen() ? ('px-24-arrow-up' satisfies SvgIcons) : ('px-24-arrow-down' satisfies SvgIcons),
  );

  protected readonly prefixClassBase =
    'flex h-[18px] w-[18px] select-none items-center justify-center rounded-md text-h8 font-bold uppercase text-text-white';

  protected readonly statusOptions: StatusOption[] = [
    {
      labelKey: 'common_treatment_status_proposed',
      value: ProgressNoteStatus.P,
      bgClass: 'bg-states-red shadow-item-status-red',
    },
    {
      labelKey: 'common_treatment_status_existing',
      value: ProgressNoteStatus.E,
      bgClass: 'bg-secondary-green shadow-item-status-green',
    },
    {
      labelKey: 'common_treatment_status_completed',
      value: ProgressNoteStatus.C,
      bgClass: 'bg-primary-blue shadow-item-status-blue',
    },
  ];

  protected readonly dropdownOptions = computed<DropdownItem<ProgressNoteStatus>[]>(() => {
    return this.statusOptions.map((option) => ({
      value: option.value,
      label: this.translateService.instant(option.labelKey),
      prefixData: { bgClass: option.bgClass, label: option.value[0] },
    }));
  });

  protected onDropdownOpenChange(isOpen: boolean): void {
    this.dropdownOpen.set(isOpen);
  }
  protected onStatusSelect(status: ProgressNoteStatus | (ProgressNoteStatus | null)[] | null): void {
    if (status && !Array.isArray(status)) {
      this.statusChange.emit(status);
    }
  }

  protected getStatusOption(status: ProgressNoteStatus): StatusOption | undefined {
    return this.statusOptions.find((option) => option.value === status);
  }
}
